import type React from 'react';

import {
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Textarea,
} from '@zillow/constellation-10';

import type { Section } from '../../../../../models';
import { OTHER_METHOD_VALUE } from '../../../../../constants';
import CurrencyInput from '../../../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  administrativeFeeField?: string;
  listBoxField: string;
  otherMethodDescriptionField: string;
  otherMethodField: string;
  setSectionValues: (keyValuePairs: object) => void;
  options: Array<{ value: string; label: string }>;
  utilityName: string;
};

export default function UtilitiesCalculation({
  section,
  setSectionValues,
  administrativeFeeField,
  listBoxField,
  options,
  otherMethodDescriptionField,
  utilityName,
  otherMethodField,
}: Props): JSX.Element {
  function onAdministrativeFeeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [administrativeFeeField]: value,
    });
  }

  function onRadioChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;
    if (value === OTHER_METHOD_VALUE) {
      setSectionValues({
        [listBoxField]: value,
        [otherMethodField]: true,
      });
    } else {
      setSectionValues({
        [listBoxField]: value,
        [otherMethodDescriptionField]: null,
        [otherMethodField]: false,
      });
    }
  }

  function onOtherMethodDescriptionChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [otherMethodDescriptionField]: value,
    });
  }

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs">
        {utilityName}
      </Heading>
      {administrativeFeeField && section.getField(administrativeFeeField) && (
        <FormField
          css={{ marginBottom: 'layout.tight' }}
          label={<Label optional>What is the administrative fee?</Label>}
          control={
            <CurrencyInput
              value={section.getFieldValue(administrativeFeeField)}
              onChange={onAdministrativeFeeChange}
              onBlur={onAdministrativeFeeChange}
              endAdornment="per month"
            />
          }
          description={
            <FormHelp>
              This should reflect actual administrative costs that you have incurred in your use of a ratio billing
              system.
            </FormHelp>
          }
        />
      )}
      <FieldSet
        required
        legend={<Legend>How did you calculate the monthly amount for {utilityName.toLowerCase()}?</Legend>}
      >
        {options.map((option) => (
          <LabeledControl
            key={option.value}
            label={<Label>{option.label}</Label>}
            control={
              <Radio
                name={`${listBoxField}-calculation`}
                value={option.value}
                checked={section.getFieldValue(listBoxField) === option.value}
                onChange={onRadioChange}
              />
            }
          />
        ))}
      </FieldSet>
      {section.getFieldValue(otherMethodField) && (
        <FormField
          required
          css={{ marginTop: 'layout.tight' }}
          label={<Label>Describe this other method</Label>}
          control={
            <Textarea
              css={{ resize: 'vertical' }}
              value={section.getFieldValue(otherMethodDescriptionField)}
              onChange={onOtherMethodDescriptionChange}
            />
          }
        />
      )}
    </>
  );
}
